/**
 * CSS-in-JS strings for the embed widget.
 * No Tailwind, no React — plain string templates injected into Shadow DOM.
 *
 * Brand primary color is passed at runtime from data-cal-color or fetched brand.
 */

export const DEFAULT_COLOR = '#1A365D';

/** Modal backdrop + container styles, scoped to Shadow DOM. */
export function getModalStyles(primaryColor: string): string {
  return `
    *,*::before,*::after{box-sizing:border-box}

    :host{display:contents}

    .cal-backdrop{
      position:fixed;
      inset:0;
      background:rgba(0,0,0,0.55);
      z-index:2147483646;
      display:flex;
      align-items:center;
      justify-content:center;
      padding:16px;
      animation:calFadeIn 150ms ease;
    }

    .cal-modal{
      position:relative;
      background:#fff;
      border-radius:12px;
      box-shadow:0 24px 60px rgba(0,0,0,0.25);
      width:100%;
      max-width:520px;
      max-height:calc(100dvh - 32px);
      overflow:hidden;
      display:flex;
      flex-direction:column;
      animation:calSlideUp 180ms cubic-bezier(0.22,1,0.36,1);
    }

    .cal-modal-header{
      display:flex;
      align-items:center;
      justify-content:flex-end;
      padding:10px 14px 0;
      flex-shrink:0;
    }

    .cal-close-btn{
      border:none;
      background:transparent;
      cursor:pointer;
      width:32px;
      height:32px;
      border-radius:50%;
      display:flex;
      align-items:center;
      justify-content:center;
      color:#6b7280;
      font-size:18px;
      line-height:1;
      transition:background 120ms,color 120ms;
    }
    .cal-close-btn:hover{background:#f3f4f6;color:#111827}
    .cal-close-btn:focus-visible{
      outline:2px solid ${primaryColor};
      outline-offset:2px;
    }

    .cal-iframe-wrap{
      flex:1 1 auto;
      overflow:hidden;
      position:relative;
      min-height:200px;
    }

    @keyframes calFadeIn{from{opacity:0}to{opacity:1}}
    @keyframes calSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}

    @media(prefers-reduced-motion:reduce){
      .cal-backdrop,.cal-modal{animation:none}
    }

    @media(max-width:540px){
      .cal-backdrop{align-items:flex-end;padding:0}
      .cal-modal{max-width:100%;border-bottom-left-radius:0;border-bottom-right-radius:0;max-height:92dvh}
    }
  `;
}

/** Inline embed container styles — simple wrapper. */
export function getInlineStyles(): string {
  return `
    *{box-sizing:border-box}
    :host{display:block;width:100%;height:100%}
    .cal-inline-wrap{width:100%;height:100%;position:relative}
  `;
}

/** Styles for the iframe element itself (shared by modal + inline). */
export function getIframeStyles(): string {
  return `
    width:100%;
    height:100%;
    border:none;
    display:block;
  `;
}

/** Loading spinner shown while iframe loads. */
export function getSpinnerHtml(primaryColor: string): string {
  return `
    <div style="
      position:absolute;inset:0;
      display:flex;align-items:center;justify-content:center;
      background:#fff;
    ">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none"
           style="animation:calSpin 0.8s linear infinite">
        <circle cx="18" cy="18" r="15" stroke="#e5e7eb" stroke-width="3"/>
        <path d="M18 3a15 15 0 0 1 15 15" stroke="${primaryColor}" stroke-width="3" stroke-linecap="round"/>
        <style>@keyframes calSpin{to{transform:rotate(360deg)}}</style>
      </svg>
    </div>
  `;
}
